import React, { useReducer } from 'react';
import { IconButton } from 'rsuite';
import { Icon } from '@rsuite/icons';
import { FaTags } from 'react-icons/fa';
import { PricingAvailabilityUpdateResult } from '../../api/apiTypes';
import { useStoreState } from '../../store/hooks';
import { applyAvailabilityUpdate, mapToSnapshotRequest } from '../../api/pricing/pricing';
import handleError from '../../api/apiErrorHandle';
import Alert from '../Alert';
import PricingRepriceResultDialog from './PricingRepriceResultDialog';

interface State {
  confirmOpen: boolean;
  loading: boolean;
  response?: PricingAvailabilityUpdateResult;
}

const initialState: State = {
  confirmOpen: false,
  loading: false,
  response: undefined,
};

type ConfirmOpenAction = { type: 'confirm/open' };
type ConfirmCloseAction = { type: 'confirm/close' };
type RepriceStartAction = { type: 'reprice/start' };
type RepriceSuccessAction = { type: 'reprice/success', payload: PricingAvailabilityUpdateResult };
type RepriceErrorAction = { type: 'reprice/error' };
type ResultCloseAction = { type: 'result/close' };
type Action =
  | ConfirmOpenAction
  | ConfirmCloseAction
  | RepriceStartAction
  | RepriceSuccessAction
  | RepriceErrorAction
  | ResultCloseAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'confirm/open':
      return { ...state, confirmOpen: true };
    case 'confirm/close':
      return { ...state, confirmOpen: false };
    case 'reprice/start':
      return { confirmOpen: false, loading: true, response: undefined };
    case 'reprice/success':
      return { ...state, loading: false, response: action.payload };
    case 'reprice/error':
      return { ...state, loading: false };
    case 'result/close':
      return { ...state, response: undefined };
    default:
      return state;
  }
}

export default function PricingReprice() {
  const currentFilter = useStoreState(state => state.pricingData.currentFilter);
  const maxPricingRequestId = useStoreState(state => state.pricingData.maxPricingRequestId);

  const [state, dispatch] = useReducer(reducer, initialState);

  async function handleReprice() {
    dispatch({ type: 'reprice/start' });
    const request = mapToSnapshotRequest(currentFilter.filter, maxPricingRequestId);
    try {
      const response = await applyAvailabilityUpdate(request);
      dispatch({ type: 'reprice/success', payload: response.data });
    } catch (error) {
      handleError(error);
      dispatch({ type: 'reprice/error' });
    }
  }

  return (
    <>
      <IconButton
        style={{ width: 220 }}
        appearance="primary"
        icon={<Icon as={FaTags} />}
        placement="right"
        onClick={() => dispatch({ type: 'confirm/open' })}
      >
        Reprice on Marketplaces
      </IconButton>

      <Alert
        isOpen={state.confirmOpen}
        onConfirm={() => handleReprice()}
        onClose={() => dispatch({ type: 'confirm/close' })}
        title="Confirmation"
        confirmLabel="I know what I'm doing"
        cancelLabel="Abort"
      >
        <p>
          I confirm that I want to send the calculated prices for the filtered offers to the
          marketplaces.
        </p>
      </Alert>

      <PricingRepriceResultDialog
        isLoading={state.loading}
        response={state.response}
        onClose={() => dispatch({ type: 'result/close' })}
      />
    </>
  );
}
